import React, { createContext, useContext, useState, useEffect } from 'react';
import { postData, setLogoutFunction } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    // Initialize user state from localStorage if available
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Set loading to false since we don't have a session check endpoint
    setLoading(false);
  }, []);

  // Update localStorage whenever user state changes
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  const logout = () => {
    setUser(null);
    toast.success('Logged out successfully');
    navigate('/login');
  };

  // Set the logout function in the API utility
  useEffect(() => {
    setLogoutFunction(logout);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await postData('/entity/admin/login', { username, password });
      if (response?.data) {
        setUser(response.data);
        toast.success('Login successful');
        navigate('/dashboard');
        return { success: true };
      }
      const message =
        (typeof response?.message === 'string' && response.message.trim()) ||
        'Login failed';
      return { success: false, error: message };
    } catch (error) {
      console.error('Login error:', error);
      const data = error?.data;
      const fromApi =
        data &&
        typeof data === 'object' &&
        (data.message || data.error);
      const message =
        (typeof fromApi === 'string' && fromApi) ||
        error?.message ||
        'Unable to sign in. Please try again.';
      return { success: false, error: String(message) };
    }
  };

  const value = {
    user,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 